import React from "react";
import { Radio as RadioIcon } from "lucide-react";

/**
 * LiveNowBadge — small "ON AIR" pill for stations / creator cards.
 *
 * Two variants
 * ------------
 *   variant="onair"  red pulsing pill, used on radio station cards
 *                    when a host is live on that station
 *   variant="live"   white-ringed red dot + "LIVE" text, used on
 *                    creator avatars / DJ tiles when the creator's
 *                    `live_streams.is_live` is true
 *
 * Both variants are pure presentation — the parent decides when to render.
 * For radio stations, fetch /api/radio/live-now once at the page level and
 * pass `liveByStationSlug[s.slug]` as truthy. For creators, look up the
 * creator id in the live_streams cache.
 */
export default function LiveNowBadge({ variant = "onair", label, className = "", hostName, hostAvatar, listeners }) {
  if (variant === "live") {
    return (
      <span
        data-testid="live-badge-live"
        className={`inline-flex items-center gap-1 bg-red-600 text-white text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded shadow-md ${className}`}
        title={hostName ? `${hostName} is live` : "Live now"}
      >
        <span className="relative inline-block w-1.5 h-1.5">
          <span className="absolute inset-0 rounded-full bg-white animate-ping opacity-75" />
          <span className="absolute inset-0 rounded-full bg-white" />
        </span>
        {label || "LIVE"}
      </span>
    );
  }

  // "onair" variant — for radio station tiles
  return (
    <span
      data-testid="live-badge-onair"
      className={`inline-flex items-center gap-1.5 bg-red-600 text-white text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full shadow-md ${className}`}
      title={hostName ? `${hostName} is on air right now` : "Host is on air right now"}
    >
      <RadioIcon className="w-3 h-3 animate-pulse" />
      {label || "ON AIR"}
      {typeof listeners === "number" && listeners > 0 && (
        <span className="text-white/90 font-bold">· {listeners}</span>
      )}
      {hostAvatar && (
        <img
          src={hostAvatar}
          alt=""
          className="w-3.5 h-3.5 rounded-full border border-white/60 object-cover -mr-0.5"
        />
      )}
    </span>
  );
}
